import React, { useEffect, useState } from 'react';
import { BsCalendar3, BsClipboard2Fill, BsClipboardCheck, BsFillPersonFill, BsHouseFill, BsTools, BsChevronDown, BsList, BsBugFill, BsShieldCheck, BsClockHistory, BsGearFill, BsShieldLock, BsGlobe, BsCameraVideo } from 'react-icons/bs';
import { Link, useLocation } from 'react-router-dom';
import logo from '../../assets/img/logo.png';
import { FaCamera, FaUniversity, FaUserCheck, FaUserShield } from 'react-icons/fa';
import logotipo_detic from '../../assets/img/logotipo_detic.png';
import { useSidebar } from '../../contexts/SidebarContext';
import { StatusRecurso } from '../../model/Candidato/Recurso';
import { PermissaoEnum } from '../../model/Usuario/Permissao';
import Usuario from '../../model/Usuario/Usuario';
import useBancaTecnicoStore from '../../stores/bancaTecnicoStore';
import useRecursoStore from '../../stores/useRecursoStore';
import useUserStore from '../../stores/userStore';
import { BancaResponseTecnicoPage, BancaViewModel } from '../../viewmodel/banca/BancaViewModel';
import CandidatoSolicitacaoSensivelViewModel from '../../viewmodel/CandidatoSolicitacaoSensivelViewModel';
import BackendErrorMessage from '../BackendErrorMessage';
import Badge from '../Badge';
import PermissaoWrapper from '../PermissaoWrapper';
import styles from './SideBar.module.scss';

interface SideBarProps {
  user: Usuario;
}

const SideBar: React.FC<SideBarProps> = ({ user }) => {
  const location = useLocation();
  const usuario = useUserStore((state) => state.usuario);
  const { recursos, getRecursosPendentes } = useRecursoStore();
  const { setBancaTecnico, videoUploadRequests, setVideoUploadRequests } = useBancaTecnicoStore();
  const { collapsed, setCollapsed } = useSidebar();
  const [openSubMenus, setOpenSubMenus] = useState<Set<string>>(new Set());
  const [hoveredMenu, setHoveredMenu] = useState<string | null>(null);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const [submenuPosition, setSubmenuPosition] = useState<{ top: number } | null>(null);
  const [solicitacoesSensivelPendentes, setSolicitacoesSensivelPendentes] = useState<number>(0);

  const handleMenuEnter = (menuId: string, event: React.MouseEvent) => {
    if (hoverTimeout) clearTimeout(hoverTimeout);
    const rect = event.currentTarget.getBoundingClientRect();
    setSubmenuPosition({ top: rect.top });
    setHoveredMenu(menuId);
  };

  const handleMenuLeave = () => {
    const timeout = setTimeout(() => {
      setHoveredMenu(null);
      setSubmenuPosition(null);
    }, 150);
    setHoverTimeout(timeout);
  };

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  const toggleSubMenu = (menuId: string) => {
    setOpenSubMenus(prev => {
      const newSet = new Set(prev);
      if (newSet.has(menuId)) {
        newSet.delete(menuId);
      } else {
        newSet.add(menuId);
      }
      return newSet;
    });
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [setCollapsed]);

  useEffect(() => {
    if (usuario?.perfil?.permissoes.some(p => p.nome.startsWith(PermissaoEnum.UPLOAD_VIDEOS + '-'))) {
      fetchBancasUploadVideo();
    }
    if (usuario?.perfil?.permissoes.some(p => p.nome.startsWith(PermissaoEnum.CONSULTAR_RECURSO))) {
      fetchRecursosPendentes();
    }
    if (usuario?.perfil?.permissoes.some(p => p.nome === `ROLE_${PermissaoEnum.ADMINISTRADOR}` || p.nome === PermissaoEnum.ADMINISTRADOR)) {
      fetchSolicitacoesSensivelPendentes();
    }
  }, [usuario]);

  const fetchBancasUploadVideo = async (): Promise<BancaResponseTecnicoPage[]> => {
    try {
      const bancas = await BancaViewModel.getBancasTecnicoPage();
      let numero = 0;
      bancas.forEach(b => {
        if (b.numeroCandidatosComVotacaoFechada) {
          numero += b.numeroCandidatosComVotacaoFechada;
        }
      });
      setBancaTecnico(bancas);
      setVideoUploadRequests(numero);
    } catch (error) {
      console.log(BackendErrorMessage(error));
    }
    return [];
  };

  const fetchRecursosPendentes = async (): Promise<null> => {
    try {
      await getRecursosPendentes();
    } catch (error) {
      console.log(BackendErrorMessage(error));
    }
    return null;
  };

  const fetchSolicitacoesSensivelPendentes = async (): Promise<void> => {
    try {
      const total = await CandidatoSolicitacaoSensivelViewModel.contarPendentes();
      setSolicitacoesSensivelPendentes(total);
    } catch (error) {
      console.log(BackendErrorMessage(error));
    }
  };

  return (
    <>
      {!collapsed && window.innerWidth <= 768 && (
        <div 
          className={styles.backdrop}
          onClick={() => setCollapsed(true)}
        />
      )}
      
      <div className={`${styles.sidebar} ${collapsed ? styles.collapsed : ''}`}>
        <div className={styles.header}>
          <button 
            className={styles.toggleButton}
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            <BsList />
          </button>
        </div>

        <nav className={styles.nav}>
          {!collapsed && (
            <>
              <Link to="/" className={styles.brandItem}>
                <img src={logo} alt="Logo Unicamp" className={styles.brandLogo} />
                <div className={styles.brandText}>
                  <span className={styles.brandTitle}>Identifica</span>
                  <span className={styles.brandSubtitle}>Sistema de Heteroidentificação de Cotas Étnico-Raciais</span>
                </div>
              </Link>
              <div className={styles.divider}></div>
            </>
          )}

          <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.CONSULTAR_PROCESSO]}>
            <Link 
              to="/processos" 
              className={`${styles.menuItem} ${isActive('/processos') ? styles.active : ''}`}
            >
              <FaUserCheck />
              {!collapsed && <span>Processos</span>}
            </Link>
          </PermissaoWrapper>

          <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.CONSULTAR_CRONOGRAMA]}>
            {collapsed ? (
              <div 
                className={styles.collapsedMenuWrapper}
                onMouseEnter={(e) => handleMenuEnter('cronograma', e)}
                onMouseLeave={handleMenuLeave}
              >
                <div 
                  className={`${styles.menuItem} ${isActive('/cronograma') ? styles.active : ''}`}
                >
                  <BsCalendar3 />
                </div>
                {hoveredMenu === 'cronograma' && (
                  <div 
                    className={styles.collapsedSubMenu}
                    style={submenuPosition ? { top: submenuPosition.top } : {}}
                    onMouseEnter={(e) => handleMenuEnter('cronograma', e)}
                    onMouseLeave={handleMenuLeave}
                  >
                    <Link to="/cronograma" className={`${styles.collapsedSubMenuItem} ${isActive('/cronograma') ? styles.active : ''}`}>
                      Cronograma Geral
                    </Link>
                    <Link to="/cronograma-pdf" className={`${styles.collapsedSubMenuItem} ${isActive('/cronograma-pdf') ? styles.active : ''}`}>
                      Gerar Cronograma PDF
                    </Link>
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.menuGroup}>
                <button
                  className={`${styles.menuItem} ${styles.expandable}`}
                  onClick={() => toggleSubMenu('cronograma')}
                >
                  <BsCalendar3 />
                  <span>Cronograma</span>
                  <BsChevronDown className={`${styles.chevron} ${openSubMenus.has('cronograma') ? styles.open : ''}`} />
                </button>
                {openSubMenus.has('cronograma') && (
                  <div className={styles.subMenu}>
                    <Link to="/cronograma" className={`${styles.subMenuItem} ${isActive('/cronograma') ? styles.active : ''}`}>
                      Cronograma Geral
                    </Link>
                    <Link to="/cronograma-pdf" className={`${styles.subMenuItem} ${isActive('/cronograma-pdf') ? styles.active : ''}`}>
                      Gerar Cronograma PDF
                    </Link>
                  </div>
                )}
              </div>
            )}
          </PermissaoWrapper>

          <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.ALTERAR_USUARIO]}>
            {collapsed ? (
              <div 
                className={styles.collapsedMenuWrapper}
                onMouseEnter={(e) => handleMenuEnter('perfis', e)}
                onMouseLeave={handleMenuLeave}
              >
                <div 
                  className={`${styles.menuItem} ${isActive('/usuarios/gerenciar-credenciados') ? styles.active : ''}`}
                >
                  <FaUserShield />
                </div>
                {hoveredMenu === 'perfis' && (
                  <div 
                    className={styles.collapsedSubMenu}
                    style={submenuPosition ? { top: submenuPosition.top } : {}}
                    onMouseEnter={(e) => handleMenuEnter('perfis', e)}
                    onMouseLeave={handleMenuLeave}
                  >
                    <Link to="/usuarios/gerenciar-credenciados" className={`${styles.collapsedSubMenuItem} ${isActive('/usuarios/gerenciar-credenciados') ? styles.active : ''}`}>
                      Perfis de Usuários
                    </Link>
                    <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.ADMINISTRADOR]}>
                      <Link to="/usuarios/permissoes-perfis" className={`${styles.collapsedSubMenuItem} ${isActive('/usuarios/permissoes-perfis') ? styles.active : ''}`}>
                        Permissões de Perfis
                      </Link>
                    </PermissaoWrapper>
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.menuGroup}>
                <button
                  className={`${styles.menuItem} ${styles.expandable}`}
                  onClick={() => toggleSubMenu('perfis')}
                >
                  <FaUserShield />
                  <span>Permissões</span>
                  <BsChevronDown className={`${styles.chevron} ${openSubMenus.has('perfis') ? styles.open : ''}`} />
                </button>
                {openSubMenus.has('perfis') && (
                  <div className={styles.subMenu}>
                    <Link to="/usuarios/gerenciar-credenciados" className={`${styles.subMenuItem} ${isActive('/usuarios/gerenciar-credenciados') ? styles.active : ''}`}>
                      Perfis de Usuários
                    </Link>
                    <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.ADMINISTRADOR]}>
                      <Link to="/usuarios/permissoes-perfis" className={`${styles.subMenuItem} ${isActive('/usuarios/permissoes-perfis') ? styles.active : ''}`}>
                        Permissões de Perfis
                      </Link>
                    </PermissaoWrapper>
                  </div>
                )}
              </div>
            )}
          </PermissaoWrapper>

          <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.CONSULTAR_PESSOA]}>
            {collapsed ? (
              <div 
                className={styles.collapsedMenuWrapper}
                onMouseEnter={(e) => handleMenuEnter('pessoas', e)}
                onMouseLeave={handleMenuLeave}
              >
                <div 
                  className={`${styles.menuItem} ${isActive('/pessoas/gerenciar-membros') ? styles.active : ''}`}
                >
                  <BsFillPersonFill />
                </div>
                {hoveredMenu === 'pessoas' && (
                  <div 
                    className={styles.collapsedSubMenu}
                    style={submenuPosition ? { top: submenuPosition.top } : {}}
                    onMouseEnter={(e) => handleMenuEnter('pessoas', e)}
                    onMouseLeave={handleMenuLeave}
                  >
                    <Link to="/pessoas/gerenciar-membros" className={`${styles.collapsedSubMenuItem} ${isActive('/pessoas/gerenciar-membros') ? styles.active : ''}`}>
                      Membros CAVU
                    </Link>
                    <Link to="/pessoas/gerenciar-externos" className={`${styles.collapsedSubMenuItem} ${isActive('/pessoas/gerenciar-externos') ? styles.active : ''}`}>
                      Participantes Externos
                    </Link>
                    <Link to="/composicoes" className={`${styles.collapsedSubMenuItem} ${isActive('/composicoes') ? styles.active : ''}`}>
                      Composições de Bancas
                    </Link>
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.menuGroup}>
                <button
                  className={`${styles.menuItem} ${styles.expandable}`}
                  onClick={() => toggleSubMenu('pessoas')}
                >
                  <BsFillPersonFill />
                  <span>Pessoas</span>
                  <BsChevronDown className={`${styles.chevron} ${openSubMenus.has('pessoas') ? styles.open : ''}`} />
                </button>
                {openSubMenus.has('pessoas') && (
                  <div className={styles.subMenu}>
                    <Link to="/pessoas/gerenciar-membros" className={`${styles.subMenuItem} ${isActive('/pessoas/gerenciar-membros') ? styles.active : ''}`}>
                      Membros CAVU
                    </Link>
                    <Link to="/pessoas/gerenciar-externos" className={`${styles.subMenuItem} ${isActive('/pessoas/gerenciar-externos') ? styles.active : ''}`}>
                      Participantes Externos
                    </Link>
                    <Link to="/composicoes" className={`${styles.subMenuItem} ${isActive('/composicoes') ? styles.active : ''}`}>
                      Composições de Bancas
                    </Link>
                  </div>
                )}
              </div>
            )}
          </PermissaoWrapper>

          <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.CADASTRAR_UNIDADE]}>
            {collapsed ? (
              <div 
                className={styles.collapsedMenuWrapper}
                onMouseEnter={(e) => handleMenuEnter('unidades', e)}
                onMouseLeave={handleMenuLeave}
              >
                <div 
                  className={`${styles.menuItem} ${isActive('/unidades/cadastrar') ? styles.active : ''}`}
                >
                  <FaUniversity />
                </div>
                {hoveredMenu === 'unidades' && (
                  <div 
                    className={styles.collapsedSubMenu}
                    style={submenuPosition ? { top: submenuPosition.top } : {}}
                    onMouseEnter={(e) => handleMenuEnter('unidades', e)}
                    onMouseLeave={handleMenuLeave}
                  >
                    <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.CADASTRAR_UNIDADE]}>
                      <Link to="/unidades/cadastrar" className={`${styles.collapsedSubMenuItem} ${isActive('/unidades/cadastrar') ? styles.active : ''}`}>
                        Cadastrar Unidades
                      </Link>
                    </PermissaoWrapper>
                    <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.ATUALIZAR_UNIDADE]}>
                      <Link to="/unidades/editar" className={`${styles.collapsedSubMenuItem} ${isActive('/unidades/editar') ? styles.active : ''}`}>
                        Editar Unidades
                      </Link>
                    </PermissaoWrapper>
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.menuGroup}>
                <button
                  className={`${styles.menuItem} ${styles.expandable}`}
                  onClick={() => toggleSubMenu('unidades')}
                >
                  <FaUniversity />
                  <span>Unidades</span>
                  <BsChevronDown className={`${styles.chevron} ${openSubMenus.has('unidades') ? styles.open : ''}`} />
                </button>
                {openSubMenus.has('unidades') && (
                  <div className={styles.subMenu}>
                    <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.CADASTRAR_UNIDADE]}>
                      <Link to="/unidades/cadastrar" className={`${styles.subMenuItem} ${isActive('/unidades/cadastrar') ? styles.active : ''}`}>
                        Cadastrar Unidades
                      </Link>
                    </PermissaoWrapper>
                    <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.ATUALIZAR_UNIDADE]}>
                      <Link to="/unidades/editar" className={`${styles.subMenuItem} ${isActive('/unidades/editar') ? styles.active : ''}`}>
                        Editar Unidades
                      </Link>
                    </PermissaoWrapper>
                  </div>
                )}
              </div>
            )}
          </PermissaoWrapper>

          <PermissaoWrapper permissoesRequiridas={[]}>
            {collapsed ? (
              <div 
                className={styles.collapsedMenuWrapper}
                onMouseEnter={(e) => handleMenuEnter('contas', e)}
                onMouseLeave={handleMenuLeave}
              >
                <div 
                  className={`${styles.menuItem} ${isActive('/contas-servico/solicitar') ? styles.active : ''}`}
                >
                  <BsTools />
                </div>
                {hoveredMenu === 'contas' && (
                  <div 
                    className={styles.collapsedSubMenu}
                    style={submenuPosition ? { top: submenuPosition.top } : {}}
                    onMouseEnter={(e) => handleMenuEnter('contas', e)}
                    onMouseLeave={handleMenuLeave}
                  >
                    <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.ADMINISTRADOR]}>
                      <Link to="/contas-servico" className={`${styles.collapsedSubMenuItem} ${isActive('/contas-servico') && !isActive('/contas-servico/solicitar') ? styles.active : ''}`}>
                        Gerenciar
                      </Link>
                    </PermissaoWrapper>
                    <PermissaoWrapper permissoesRequiridas={[]}>
                      <Link to="/contas-servico/solicitar" className={`${styles.collapsedSubMenuItem} ${isActive('/contas-servico/solicitar') ? styles.active : ''}`}>
                        Solicitar
                      </Link>
                    </PermissaoWrapper>
                    <Link to="/contas-servico/minhas" className={`${styles.collapsedSubMenuItem} ${isActive('/contas-servico/minhas') ? styles.active : ''}`}>
                      Minhas Contas
                    </Link>
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.menuGroup}>
                <button
                  className={`${styles.menuItem} ${styles.expandable}`}
                  onClick={() => toggleSubMenu('contas')}
                >
                  <BsTools />
                  <span>Contas de Serviço</span>
                  <BsChevronDown className={`${styles.chevron} ${openSubMenus.has('contas') ? styles.open : ''}`} />
                </button>
                {openSubMenus.has('contas') && (
                  <div className={styles.subMenu}>
                    <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.ADMINISTRADOR]}>
                      <Link to="/contas-servico" className={`${styles.subMenuItem} ${isActive('/contas-servico') && !isActive('/contas-servico/solicitar') ? styles.active : ''}`}>
                        Gerenciar
                      </Link>
                    </PermissaoWrapper>
                    <PermissaoWrapper permissoesRequiridas={[]}>
                      <Link to="/contas-servico/solicitar" className={`${styles.subMenuItem} ${isActive('/contas-servico/solicitar') ? styles.active : ''}`}>
                        Solicitar
                      </Link>
                    </PermissaoWrapper>
                    <Link to="/contas-servico/minhas" className={`${styles.subMenuItem} ${isActive('/contas-servico/minhas') ? styles.active : ''}`}>
                      Minhas Contas
                    </Link>
                  </div>
                )}
              </div>
            )}
          </PermissaoWrapper>

          <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.UPLOAD_VIDEOS]}>
            <Link 
              to="/tecnico/bancas" 
              className={`${styles.menuItem} ${isActive('/tecnico/bancas') ? styles.active : ''}`}
            >
              <FaCamera />
              {!collapsed && (
                <span>
                  Upload Vídeos {videoUploadRequests > 0 && <Badge>{videoUploadRequests}</Badge>}
                </span>
              )}
              {collapsed && videoUploadRequests > 0 && (
                <div className={styles.collapsedBadge}>{videoUploadRequests}</div>
              )}
            </Link>
          </PermissaoWrapper>


          <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.CADASTRAR_BANCA]}>
            <Link 
              to="/auditoria-heteroidentificacao" 
              className={`${styles.menuItem} ${isActive('/auditoria-heteroidentificacao') ? styles.active : ''}`}
            >
              <BsClipboard2Fill />
              {!collapsed && <span>Auditoria</span>}
            </Link>
          </PermissaoWrapper>

          <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.ADMINISTRADOR]}>
            {collapsed ? (
              <div 
                className={styles.collapsedMenuWrapper}
                onMouseEnter={(e) => handleMenuEnter('admin', e)}
                onMouseLeave={handleMenuLeave}
              >
                <div 
                  className={`${styles.menuItem} ${(isActive('/auditoria') || isActive('/unidades') || isActive('/integracao-siga/logs') || isActive('/candidatos/solicitacoes-sensiveis') || isActive('/configuracoes/acesso-candidato') || isActive('/configuracoes/foto') || isActive('/admin/recuperar-video')) ? styles.active : ''}`}
                >
                  <BsShieldLock />
                  {solicitacoesSensivelPendentes > 0 && (
                    <div className={styles.collapsedBadge}>{solicitacoesSensivelPendentes}</div>
                  )}
                </div>
                {hoveredMenu === 'admin' && (
                  <div 
                    className={styles.collapsedSubMenu}
                    style={submenuPosition ? { top: submenuPosition.top } : {}}
                    onMouseEnter={(e) => handleMenuEnter('admin', e)}
                    onMouseLeave={handleMenuLeave}
                  >
                    <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.AUDITORIA]}>
                      <Link to="/auditoria" className={`${styles.collapsedSubMenuItem} ${isActive('/auditoria') ? styles.active : ''}`}>
                        <BsClipboardCheck /> Auditoria Entidades
                      </Link>
                    </PermissaoWrapper>
                    <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.CADASTRAR_UNIDADE]}>
                      <Link to="/unidades/cadastrar" className={`${styles.collapsedSubMenuItem} ${isActive('/unidades/cadastrar') ? styles.active : ''}`}>
                        <FaUniversity /> Cadastrar Unidades
                      </Link>
                    </PermissaoWrapper>
                    <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.ATUALIZAR_UNIDADE]}>
                      <Link to="/unidades/editar" className={`${styles.collapsedSubMenuItem} ${isActive('/unidades/editar') ? styles.active : ''}`}>
                        <FaUniversity /> Editar Unidades
                      </Link>
                    </PermissaoWrapper>
                    <Link to="/integracao-siga/logs" className={`${styles.collapsedSubMenuItem} ${isActive('/integracao-siga/logs') ? styles.active : ''}`}>
                      <BsBugFill /> Logs Integração SIGA
                    </Link>
                    <Link to="/candidatos/solicitacoes-sensiveis" className={`${styles.collapsedSubMenuItem} ${isActive('/candidatos/solicitacoes-sensiveis') ? styles.active : ''}`}>
                      <BsTools /> Solicitações Candidato {solicitacoesSensivelPendentes > 0 && <Badge>{solicitacoesSensivelPendentes}</Badge>}
                    </Link>
                    <Link to="/configuracoes/acesso-candidato" className={`${styles.collapsedSubMenuItem} ${isActive('/configuracoes/acesso-candidato') ? styles.active : ''}`}>
                      <BsGearFill /> Config. Candidato
                    </Link>
                    <Link to="/configuracoes/foto" className={`${styles.collapsedSubMenuItem} ${isActive('/configuracoes/foto') ? styles.active : ''}`}>
                      <BsGearFill /> Config. Foto
                    </Link>
                    <Link to="/paginas-publicas" className={`${styles.collapsedSubMenuItem} ${isActive('/paginas-publicas') ? styles.active : ''}`}>
                      <BsGlobe /> Páginas Públicas
                    </Link>
                    <Link to="/admin/recuperar-video" className={`${styles.collapsedSubMenuItem} ${isActive('/admin/recuperar-video') ? styles.active : ''}`}>
                      <BsCameraVideo /> Recuperar Vídeo
                    </Link>
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.menuGroup}>
                <button
                  className={`${styles.menuItem} ${styles.expandable}`}
                  onClick={() => toggleSubMenu('admin')}
                >
                  <BsShieldLock />
                  <span>Administração</span>
                  <BsChevronDown className={`${styles.chevron} ${openSubMenus.has('admin') ? styles.open : ''}`} />
                </button>
                {openSubMenus.has('admin') && (
                  <div className={styles.subMenu}>
                    <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.AUDITORIA]}>
                      <Link to="/auditoria" className={`${styles.subMenuItem} ${isActive('/auditoria') ? styles.active : ''}`}>
                        <BsClipboardCheck /> Auditoria Entidades
                      </Link>
                    </PermissaoWrapper>
                    <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.CADASTRAR_UNIDADE]}>
                      <Link to="/unidades/cadastrar" className={`${styles.subMenuItem} ${isActive('/unidades/cadastrar') ? styles.active : ''}`}>
                        <FaUniversity /> Cadastrar Unidades
                      </Link>
                    </PermissaoWrapper>
                    <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.ATUALIZAR_UNIDADE]}>
                      <Link to="/unidades/editar" className={`${styles.subMenuItem} ${isActive('/unidades/editar') ? styles.active : ''}`}>
                        <FaUniversity /> Editar Unidades
                      </Link>
                    </PermissaoWrapper>
                    <Link to="/integracao-siga/logs" className={`${styles.subMenuItem} ${isActive('/integracao-siga/logs') ? styles.active : ''}`}>
                      <BsBugFill /> Logs Integração SIGA
                    </Link>
                    <Link to="/candidatos/solicitacoes-sensiveis" className={`${styles.subMenuItem} ${isActive('/candidatos/solicitacoes-sensiveis') ? styles.active : ''}`}>
                      <BsTools /> Solicitações Candidato {solicitacoesSensivelPendentes > 0 && <Badge>{solicitacoesSensivelPendentes}</Badge>}
                    </Link>
                    <Link to="/configuracoes/acesso-candidato" className={`${styles.subMenuItem} ${isActive('/configuracoes/acesso-candidato') ? styles.active : ''}`}>
                      <BsGearFill /> Config. Candidato
                    </Link>
                    <Link to="/configuracoes/foto" className={`${styles.subMenuItem} ${isActive('/configuracoes/foto') ? styles.active : ''}`}>
                      <BsGearFill /> Config. Foto
                    </Link>
                    <Link to="/paginas-publicas" className={`${styles.subMenuItem} ${isActive('/paginas-publicas') ? styles.active : ''}`}>
                      <BsGlobe /> Páginas Públicas
                    </Link>
                    <Link to="/admin/recuperar-video" className={`${styles.subMenuItem} ${isActive('/admin/recuperar-video') ? styles.active : ''}`}>
                      <BsCameraVideo /> Recuperar Vídeo
                    </Link>
                  </div>
                )}
              </div>
            )}
          </PermissaoWrapper>

          <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.DAC]}>
            <Link 
              to="/siga-dac" 
              className={`${styles.menuItem} ${isActive('/siga-dac') ? styles.active : ''}`}
            >
              <img src="/dac.svg" alt="DAC" style={{ width: '20px', height: '20px', transform: 'scaleX(3.5)', marginLeft: '35px' }} />
              {!collapsed && <span>SIGA/DAC</span>}
            </Link>
          </PermissaoWrapper>

          {/* Link público - sem permissões necessárias */}
          <Link 
            to="/verificacao" 
            className={`${styles.menuItem} ${isActive('/verificacao') ? styles.active : ''}`}
          >
            <BsShieldCheck />
            {!collapsed && <span>Verificar Documento</span>}
          </Link>


          <Link 
            to="/api-docs" 
            className={`${styles.menuItem} ${isActive('/api-docs') ? styles.active : ''}`}
          >
            <BsGlobe />
            {!collapsed && <span>API de Integração</span>}
          </Link>
          <Link 
            to="/changelog" 
            className={`${styles.menuItem} ${isActive('/changelog') ? styles.active : ''}`}
          >
            <BsClockHistory />
            {!collapsed && <span>Histórico de Atualizações</span>}
          </Link>
        </nav>

        <div className={styles.footer}>
          {!collapsed && (
            <>
              <p>Desenvolvido por</p>
              <a href="https://detic.unicamp.br/" target="_blank" rel="noopener noreferrer">
                <img src={logotipo_detic} alt="DETIC Logo" />
              </a>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default SideBar;
